import {
  adminLeadGuestConversionPreflightRevisionMaxLength,
  leadGuestDispositions,
  leadGuestDuplicateReviewDecisions,
  type AdminLeadDetail,
  type AdminLeadGuestConversionDuplicateReview,
  type AdminLeadGuestConversionOutcome,
  type AdminLeadGuestConversionPreflightRevision,
  type AdminLeadGuestConversionRecord,
  type AdminLeadGuestConversionRequest,
  type LeadGuestDuplicateReviewDecision,
} from "@whitehaven/contracts";
import {
  adminLeadDuplicateSummariesEqual,
  isAdminLeadDuplicateCandidateSetRevision,
  isAdminLeadDuplicateSummaryConsistent,
} from "./admin-lead-duplicate-model";
import { isSafeLeadIdPathSegment } from "./lead-detail-route-model";

const guestDispositionSet: ReadonlySet<string> = new Set(leadGuestDispositions);
const duplicateReviewDecisionSet: ReadonlySet<string> = new Set(
  leadGuestDuplicateReviewDecisions,
);

function hasExactKeys(
  value: Readonly<Record<string, unknown>>,
  expected: readonly string[],
): boolean {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

function isCanonicalTimestamp(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }
  const timestamp = new Date(value);
  return (
    Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value
  );
}

function isSafeOpaqueId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= 128 &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

function isCandidateCount(value: unknown): value is number {
  return (
    Number.isSafeInteger(value) &&
    typeof value === "number" &&
    value >= 0 &&
    value <= 1_000_000
  );
}

export function isAdminLeadGuestConversionPreflightRevision(
  value: unknown,
): value is AdminLeadGuestConversionPreflightRevision {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= adminLeadGuestConversionPreflightRevisionMaxLength &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

export function duplicateReviewDecisionFor(
  candidateContactCount: number,
): LeadGuestDuplicateReviewDecision {
  return candidateContactCount === 0
    ? "NO_POSSIBLE_MATCHES"
    : "CONFIRMED_DISTINCT_CONTACT";
}

export function isLeadGuestDuplicateReviewDecisionConsistent(
  decision: unknown,
  candidateContactCount: unknown,
): decision is LeadGuestDuplicateReviewDecision {
  return (
    typeof decision === "string" &&
    duplicateReviewDecisionSet.has(decision) &&
    isCandidateCount(candidateContactCount) &&
    decision === duplicateReviewDecisionFor(candidateContactCount)
  );
}

export function isAdminLeadGuestConversionRecordConsistent(
  value: unknown,
): value is AdminLeadGuestConversionRecord {
  if (typeof value !== "object" || value === null) return false;
  const record = value as unknown as Readonly<Record<string, unknown>>;
  return (
    hasExactKeys(record, [
      "conversionId",
      "leadId",
      "guestId",
      "guestDisposition",
      "duplicateReviewDecision",
      "duplicateCandidateCount",
      "convertedAt",
    ]) &&
    isSafeOpaqueId(record.conversionId) &&
    typeof record.leadId === "string" &&
    isSafeLeadIdPathSegment(record.leadId) &&
    isSafeOpaqueId(record.guestId) &&
    typeof record.guestDisposition === "string" &&
    guestDispositionSet.has(record.guestDisposition) &&
    isLeadGuestDuplicateReviewDecisionConsistent(
      record.duplicateReviewDecision,
      record.duplicateCandidateCount,
    ) &&
    isCanonicalTimestamp(record.convertedAt)
  );
}

export function isAdminLeadGuestConversionDuplicateReviewConsistent(
  value: unknown,
): value is AdminLeadGuestConversionDuplicateReview {
  if (typeof value !== "object" || value === null) return false;
  const review = value as unknown as Readonly<Record<string, unknown>>;
  return (
    hasExactKeys(review, ["leadId", "candidateSetRevision", "summary"]) &&
    typeof review.leadId === "string" &&
    isSafeLeadIdPathSegment(review.leadId) &&
    isAdminLeadDuplicateCandidateSetRevision(review.candidateSetRevision) &&
    isAdminLeadDuplicateSummaryConsistent(review.summary)
  );
}

export function isAdminLeadGuestConversionStateConsistent(
  detail: AdminLeadDetail,
): boolean {
  const { guestConversion, guestConversionPreflightRevision, summary } = detail;

  if (guestConversion === null) {
    return summary.leadRecordStatus === "ACTIVE" &&
      summary.contactRecordStatus === "ACTIVE"
      ? isAdminLeadGuestConversionPreflightRevision(
          guestConversionPreflightRevision,
        )
      : guestConversionPreflightRevision === null;
  }

  return (
    guestConversionPreflightRevision === null &&
    isAdminLeadGuestConversionRecordConsistent(guestConversion) &&
    guestConversion.leadId === summary.leadId &&
    guestConversion.convertedAt >= summary.createdAt &&
    guestConversion.convertedAt <= summary.updatedAt
  );
}

export function canPrepareAdminLeadGuestConversion(
  detail: AdminLeadDetail,
  review: AdminLeadGuestConversionDuplicateReview | null,
): review is AdminLeadGuestConversionDuplicateReview {
  return (
    detail.guestConversion === null &&
    detail.summary.leadRecordStatus === "ACTIVE" &&
    detail.summary.contactRecordStatus === "ACTIVE" &&
    isAdminLeadGuestConversionPreflightRevision(
      detail.guestConversionPreflightRevision,
    ) &&
    review !== null &&
    isAdminLeadGuestConversionDuplicateReviewConsistent(review) &&
    review.leadId === detail.summary.leadId &&
    review.summary.eligibility === "ELIGIBLE" &&
    adminLeadDuplicateSummariesEqual(
      review.summary,
      detail.summary.duplicateSummary,
    )
  );
}

export function isAdminLeadGuestConversionFailureOutcome(
  value: unknown,
): value is Extract<
  AdminLeadGuestConversionOutcome,
  { readonly kind: "conflict" | "rejected" }
> {
  if (typeof value !== "object" || value === null) return false;
  const outcome = value as unknown as Readonly<Record<string, unknown>>;
  return (
    hasExactKeys(outcome, ["kind"]) &&
    (outcome.kind === "conflict" || outcome.kind === "rejected")
  );
}

export function isConfirmedAdminLeadGuestConversionConsistent(
  detail: AdminLeadDetail,
  review: AdminLeadGuestConversionDuplicateReview,
  request: AdminLeadGuestConversionRequest,
  outcome: unknown,
): outcome is Extract<
  AdminLeadGuestConversionOutcome,
  { readonly kind: "confirmed" }
> {
  if (
    typeof outcome !== "object" ||
    outcome === null ||
    !canPrepareAdminLeadGuestConversion(detail, review)
  ) {
    return false;
  }

  const response = outcome as unknown as Readonly<Record<string, unknown>>;
  if (
    !hasExactKeys(response, ["kind", "conversion"]) ||
    response.kind !== "confirmed" ||
    !isAdminLeadGuestConversionRecordConsistent(response.conversion)
  ) {
    return false;
  }

  const conversion = response.conversion;
  return (
    request.leadId === detail.summary.leadId &&
    request.expectedUpdatedAt === detail.summary.updatedAt &&
    request.conversionPreflightRevision ===
      detail.guestConversionPreflightRevision &&
    request.expectedDuplicateCandidateSetRevision ===
      review.candidateSetRevision &&
    request.expectedDuplicateCandidateCount ===
      review.summary.candidateContactCount &&
    isLeadGuestDuplicateReviewDecisionConsistent(
      request.duplicateReviewDecision,
      request.expectedDuplicateCandidateCount,
    ) &&
    conversion.leadId === request.leadId &&
    conversion.duplicateReviewDecision === request.duplicateReviewDecision &&
    conversion.duplicateCandidateCount ===
      request.expectedDuplicateCandidateCount &&
    conversion.convertedAt > request.expectedUpdatedAt
  );
}

export function applyConfirmedAdminLeadGuestConversion(
  detail: AdminLeadDetail,
  review: AdminLeadGuestConversionDuplicateReview,
  request: AdminLeadGuestConversionRequest,
  outcome: unknown,
): AdminLeadDetail | null {
  if (
    !isConfirmedAdminLeadGuestConversionConsistent(
      detail,
      review,
      request,
      outcome,
    )
  ) {
    return null;
  }

  return {
    ...detail,
    summary: {
      ...detail.summary,
      updatedAt: outcome.conversion.convertedAt,
    },
    guestConversion: outcome.conversion,
    guestConversionPreflightRevision: null,
  };
}
